import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "./activities.scss";
import activitiesData from "../../data/activities.json";
import { Col, Container, Row } from "react-bootstrap";

const RING_COLORS = [
    "#A855F7",
    "#22D3EE",
    "#60A5FA",
    "#34D399",
    "#FBBF24",
    "#FB7185",
    "#A3E635",
    "#38BDF8"
];


function useElementSize(ref) {
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!ref.current) return;
        const ro = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setSize({ width, height });
        });
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, [ref]);

    return size;
}

function useMediaQuery(query) {
    const [matches, setMatches] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia(query).matches : false
    );

    useEffect(() => {
        const m = window.matchMedia(query);
        const onChange = () => setMatches(m.matches);
        onChange();
        m.addEventListener?.("change", onChange);
        return () => m.removeEventListener?.("change", onChange);
    }, [query]);

    return matches;
}

export default function Activities({ data = activitiesData }) {
    const items = useMemo(() => (data?.items || []).slice(0, 8), [data]);

    const [selectedId, setSelectedId] = useState(null);
    const [popupAnchor, setPopupAnchor] = useState(null); // {x, y, side}

    const isDesktop = useMediaQuery("(min-width: 981px)");

    const stageRef = useRef(null);
    const { width: stageW, height: stageH } = useElementSize(stageRef);

    const NODE_OFFSET_X = isDesktop ? -70 : -35;
    const NODE_OFFSET_Y = isDesktop ? -60 : -60;

    const cardRef = useRef(null);

    useEffect(() => {
        setSelectedId(null);
        setPopupAnchor(null);
    }, [data]);

    const selectedItem = items.find((x) => x.id === selectedId) || null;

    const layout = useMemo(() => {
        if (!stageW || !stageH) return { nodeSize: 110, positions: [], polygon: "" };

        const nodeSize = isDesktop
            ? Math.max(96, Math.min(180, stageW * 0.17))
            : Math.max(72, Math.min(118, stageW * 0.145));

        const padding = isDesktop ? 20 : 18;

        const cx = stageW / 2;
        const cy = stageH / 2;
        const radius = Math.max(0, Math.min(stageW, stageH) / 2 - nodeSize / 2 - padding);

        const count = 8;
        const startDeg = -90;
        const step = 360 / count;

        const positions = Array.from({ length: count }).map((_, i) => {
            const deg = startDeg + i * step;
            const rad = (deg * Math.PI) / 180;
            return { i, x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
        });

        const polygon = positions.map((p) => `${p.x},${p.y}`).join(" ");
        return { nodeSize, positions, polygon };
    }, [stageW, stageH, isDesktop]);

    const closeCard = () => {
        setSelectedId(null);
        setPopupAnchor(null);
    };

    const onNodeClick = (itemId, anchorX, anchorY) => {
        setSelectedId(itemId);

        if (isDesktop && stageW) {
            const side = anchorX > stageW * 0.62 ? "left" : "right";
            setPopupAnchor({ x: anchorX, y: anchorY, side });
        }
    };

    useEffect(() => {
        if (!selectedItem) return;

        const onPointerDown = (e) => {
            const t = e.target;

            if (cardRef.current && cardRef.current.contains(t)) return;
            const nodeEl = t?.closest?.(".ce-node");
            if (nodeEl) return;

            closeCard();
        };

        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [selectedItem]);

    return (
        <section id="places" className="ce-wrap">
            <Container>
                <Row>
                    <Col md={12} className="mb50">
                        <div className="section-title">Around us</div>
                        <h2>Discover Another Side of Cyprus</h2>
                    </Col>
                </Row>
            </Container>

            <div className="ce-grid">
                <div className="ce-stage" ref={stageRef} role="group" aria-label={data?.label || "Activities"}>
                    {!!stageW && !!stageH && (
                        <svg className="ce-lines" viewBox={`0 0 ${stageW} ${stageH}`} preserveAspectRatio="none" aria-hidden="true">
                            <motion.polygon
                                className="ce-poly"
                                points={layout.polygon}
                                initial={{ pathLength: 0, opacity: 0 }}
                                animate={{ pathLength: 1, opacity: 0.9 }}
                                transition={{ duration: 1.05, ease: "easeInOut" }}
                            />
                        </svg>
                    )}

                    <motion.div
                        className="ce-center"
                        initial={{ scale: 0.92, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                    >
                        <div className="ce-centerGlow" />
                        <div className="ce-centerSvg" aria-hidden="true" />

                        <div className="ce-chakras" aria-hidden="true">
                            {RING_COLORS.map((c, idx) => (
                                <span key={idx} className="ce-dot" style={{ background: c }} />
                            ))}
                        </div>
                    </motion.div>

                    {layout.positions.map(({ x, y, i }) => {
                        const item = items[i] || { id: i + 1, text: `Item ${i + 1}` };
                        const isActive = item.id === selectedId;

                        const fromCenterX = stageW / 2 - x;
                        const fromCenterY = stageH / 2 - y;

                        return (
                            <motion.button
                                key={item.id}
                                type="button"
                                className={`ce-node ${isActive ? "is-active" : ""}`}
                                style={{
                                    left: `${x + NODE_OFFSET_X}px`,
                                    top: `${y + NODE_OFFSET_Y}px`,
                                    width: `${layout.nodeSize}px`,
                                    height: `${layout.nodeSize}px`,
                                    "--glow": RING_COLORS[i],
                                    backgroundImage: item.bg ? `url(${item.bg})` : undefined,
                                }}
                                onClick={() => onNodeClick(item.id, x, y)}
                                aria-label={item.text}
                                title={item.text}
                                initial={{ opacity: 0, x: fromCenterX, y: fromCenterY, scale: 0.55 }}
                                animate={{ opacity: 1, x: 0, y: 0, scale: isActive ? 1.06 : 1 }}
                                transition={{ delay: 0.12 + i * 0.09, duration: 0.72, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ scale: 1.085 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span className="ce-pulse" aria-hidden="true" />
                                <span className="ce-nodeInner">
                  <span className="ce-nodeTitle">{item.text}</span>
                </span>
                            </motion.button>
                        );
                    })}

                    {isDesktop && (
                        <AnimatePresence>
                            {selectedItem && popupAnchor && (
                                <motion.article
                                    key={selectedItem.id}
                                    ref={cardRef}
                                    className={`ce-card ce-cardPopup ${popupAnchor.side === "left" ? "is-left" : "is-right"}`}
                                    style={{ left: `${popupAnchor.x}px`, top: `${popupAnchor.y}px` }}
                                    initial={{ opacity: 0, scale: 0.98, y: 8 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.98, y: 8 }}
                                    transition={{ duration: 0.18, ease: "easeOut" }}
                                >
                                    <div className="ce-cardMedia" style={{ backgroundImage: selectedItem.bg ? `url(${selectedItem.bg})` : undefined }}>
                                        <div className="ce-cardMediaOverlay" />
                                        <div className="ce-cardTitleRow">
                                            <div className="ce-badge" />
                                            <div>
                                                <h3 className="ce-cardTitle">{selectedItem.text}</h3>
                                                {data?.label && <p className="ce-cardSubtitle">{data.label}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ce-cardBody">
                                        <p className="ce-cardDesc">{selectedItem.desc}</p>
                                        <div className="ce-cardActions">
                                            <a href={selectedItem.link} target="_blank" className="ce-btn ghost" type="button">
                                                View in Google Maps
                                            </a>
                                            <button className="ce-btn ghost" type="button" onClick={closeCard}>
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </motion.article>
                            )}
                        </AnimatePresence>
                    )}
                </div>

                {!isDesktop && (
                    <div className="ce-panel">
                        <AnimatePresence mode="wait">
                            {selectedItem && (
                                <motion.article
                                    key={selectedItem.id}
                                    ref={cardRef}
                                    className="ce-card"
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    transition={{ duration: 0.22, ease: "easeOut" }}
                                >
                                    <div className="ce-cardMedia" style={{ backgroundImage: selectedItem.bg ? `url(${selectedItem.bg})` : undefined }}>
                                        <div className="ce-cardMediaOverlay" />
                                        <div className="ce-cardTitleRow">
                                            <div className="ce-badge" />
                                            <div>
                                                <h3 className="ce-cardTitle">{selectedItem.text}</h3>
                                                {data?.label && <p className="ce-cardSubtitle">{data.label}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ce-cardBody">
                                        <p className="ce-cardDesc">{selectedItem.text}</p>
                                        <div className="ce-cardActions">
                                            <a href={selectedItem.link} target="_blank" className="ce-btn ghost"
                                               type="button">
                                                View in Google Maps
                                            </a>
                                            <button className="ce-btn ghost" type="button" onClick={closeCard}>
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </motion.article>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </section>
    );
}
